import { ColumnState } from 'ag-grid-community';
import { create } from 'zustand';
import { HrmWorkShiftDto } from 'src/dtos/hrmWorkShift/hrmWorkShiftDto';
import {
    defaultHRM2120_R01DetailDto,
    defaultHRM2120_R01HeaderDto,
    HRM2120_R01BulkType,
    HRM2120_R01CalendarDto,
    HRM2120_R01DetailDto,
    HRM2120_R01HeaderDto,
} from './Dto_HRM2120_R01';
import { getHRM2120_R01FirstDateOfMonth } from './Util_HRM2120_R01';

/** 사원과 기준일 근무조 조회 조건이다. */
export interface HRM2120_R01Filter {
    compCd: string; // 사업장
    orgCd: string; // 부서
    empSts: string; // 재직상태
    keyword: string; // 사원번호 또는 이름
    empNm: string; // 팝업에서 선택한 사원명
    baseDt: string; // 근무조 계산 기준일
}
/** 현재 현지 날짜를 조회 기준일 문자열로 반환한다. */
const today = (): string => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
/** 사원 조회 기본 조건이다. */
export const defaultHRM2120_R01Filter: HRM2120_R01Filter = {
    compCd: '',
    orgCd: '',
    empSts: '10',
    keyword: '',
    empNm: '',
    baseDt: today(),
};
/** 일괄 배정 입력 한 줄의 값이다. */
export interface HRM2120_R01BulkForm {
    bulkType: HRM2120_R01BulkType; // 방식
    bulkShiftCd: string; // 고정 근무조
    bulkStartDt: string; // 적용시작일
}
/** 일괄 배정 기본 입력이다. */
export const defaultHRM2120_R01BulkForm = (): HRM2120_R01BulkForm => ({
    bulkType: 'FIX',
    bulkShiftCd: 'D',
    bulkStartDt: getHRM2120_R01FirstDateOfMonth(),
});

/** 근무조 배정 화면의 사원·이력·달력·일괄 입력 상태이다. */
interface Store_HRM2120_R01 {
    HRM2120_R01Filter: HRM2120_R01Filter;
    HRM2120_R01PastFilter: HRM2120_R01Filter;
    setHRM2120_R01Filter: (value: Partial<HRM2120_R01Filter>) => void;
    setHRM2120_R01PastFilter: (value: HRM2120_R01Filter) => void;
    HRM2120_R01List: HRM2120_R01HeaderDto[];
    setHRM2120_R01List: (value: HRM2120_R01HeaderDto[]) => void;
    selectedHRM2120_R01?: HRM2120_R01HeaderDto; // 현재 선택한 사원
    setSelectedHRM2120_R01: (value?: HRM2120_R01HeaderDto) => void;
    HRM2120_R01DetailList: HRM2120_R01DetailDto[];
    setHRM2120_R01DetailList: (value: HRM2120_R01DetailDto[]) => void;
    updateHRM2120_R01DetailList: (index: number, value: HRM2120_R01DetailDto) => void;
    insertHRM2120_R01DetailList: (value: HRM2120_R01DetailDto, afterValueIndex?: number) => void;
    removeHRM2120_R01DetailList: (index: number) => void;
    selectedHRM2120_R01DetailList: string[];
    setSelectedHRM2120_R01DetailList: (value: string[]) => void;
    deletedHRM2120_R01DetailList: string[];
    setDeletedHRM2120_R01DetailList: (value: string[]) => void;
    originalHRM2120_R01DetailList: HRM2120_R01DetailDto[];
    setOriginalHRM2120_R01DetailList: (value: HRM2120_R01DetailDto[]) => void;
    HRM2120_R01CalendarList: HRM2120_R01CalendarDto[];
    setHRM2120_R01CalendarList: (value: HRM2120_R01CalendarDto[]) => void;
    HRM2120_R01BulkForm: HRM2120_R01BulkForm;
    setHRM2120_R01BulkForm: (value: Partial<HRM2120_R01BulkForm>) => void;
    workShiftList: HrmWorkShiftDto[];
    setWorkShiftList: (value: HrmWorkShiftDto[]) => void;
    allWorkShiftCodes: string[];
    setAllWorkShiftCodes: (value: string[]) => void;
    columnState: ColumnState[] | null;
    detailColumnState: ColumnState[] | null;
    setColumnState: (value: ColumnState[] | null) => void;
    setDetailColumnState: (value: ColumnState[] | null) => void;
    scrollTop: number | null;
    detailScrollTop: number | null;
    setScrollTop: (value: number | null) => void;
    setDetailScrollTop: (value: number | null) => void;
    allCount: number;
    setAllCount: (value: number) => void;
    initData: () => void;
}

/** 서버 행에 화면 그리드의 순서와 식별값을 부여한다. */
const buildRows = <T extends { valueIndex: number; orderIndex: number; orderIndexSeq: number }>(
    rows: T[],
    defaults: T,
): T[] => rows.map((row, index) => ({
    ...defaults,
    ...row,
    valueIndex: index + 1,
    orderIndex: index + 1,
    orderIndexSeq: 0,
}));

/** 근무조 배정 화면의 상태를 보관한다. */
export const useHRM2120_R01Store = create<Store_HRM2120_R01>((set) => ({
    HRM2120_R01Filter: { ...defaultHRM2120_R01Filter },
    HRM2120_R01PastFilter: { ...defaultHRM2120_R01Filter },
    setHRM2120_R01Filter: value => set(state => ({
        HRM2120_R01Filter: { ...state.HRM2120_R01Filter, ...value },
    })),
    setHRM2120_R01PastFilter: value => set({ HRM2120_R01PastFilter: { ...value } }),

    HRM2120_R01List: [],
    setHRM2120_R01List: value => set({
        HRM2120_R01List: buildRows(value, defaultHRM2120_R01HeaderDto),
    }),
    selectedHRM2120_R01: undefined,
    setSelectedHRM2120_R01: value => set({ selectedHRM2120_R01: value }),

    HRM2120_R01DetailList: [],
    setHRM2120_R01DetailList: value => set({
        HRM2120_R01DetailList: buildRows(value, defaultHRM2120_R01DetailDto),
        selectedHRM2120_R01DetailList: [],
        deletedHRM2120_R01DetailList: [],
    }),
    updateHRM2120_R01DetailList: (index, value) => set(state => ({
        HRM2120_R01DetailList: state.HRM2120_R01DetailList.map(row =>
            row.valueIndex === index ? value : row),
    })),
    insertHRM2120_R01DetailList: (value, afterValueIndex) => set(state => {
        /** index의 +는 해당 행 뒤에, 액션바 등록은 목록 끝에 행을 추가한다. */
        const rows = [...state.HRM2120_R01DetailList];
        const targetIndex = rows.findIndex(row => row.valueIndex === afterValueIndex);
        const insertPosition = afterValueIndex === undefined || targetIndex < 0
            ? rows.length : targetIndex + 1;
        const nextValueIndex = Math.max(0, ...rows.map(row => row.valueIndex)) + 1;
        rows.splice(insertPosition, 0, { ...value, valueIndex: nextValueIndex });
        return { HRM2120_R01DetailList: rows };
    }),
    removeHRM2120_R01DetailList: index => set(state => ({
        HRM2120_R01DetailList: state.HRM2120_R01DetailList.filter(row => row.valueIndex !== index),
    })),
    selectedHRM2120_R01DetailList: [],
    setSelectedHRM2120_R01DetailList: value => set({ selectedHRM2120_R01DetailList: value.map(String) }),
    deletedHRM2120_R01DetailList: [],
    setDeletedHRM2120_R01DetailList: value => set({ deletedHRM2120_R01DetailList: value.map(String) }),
    originalHRM2120_R01DetailList: [],
    setOriginalHRM2120_R01DetailList: value => set({
        originalHRM2120_R01DetailList: buildRows(value, defaultHRM2120_R01DetailDto),
    }),

    HRM2120_R01CalendarList: [],
    setHRM2120_R01CalendarList: value => set({ HRM2120_R01CalendarList: value }),
    HRM2120_R01BulkForm: defaultHRM2120_R01BulkForm(),
    setHRM2120_R01BulkForm: value => set(state => ({
        HRM2120_R01BulkForm: { ...state.HRM2120_R01BulkForm, ...value },
    })),
    workShiftList: [],
    setWorkShiftList: value => set({ workShiftList: value }),
    allWorkShiftCodes: [],
    setAllWorkShiftCodes: value => set({ allWorkShiftCodes: value }),

    columnState: null,
    detailColumnState: null,
    setColumnState: value => set({ columnState: value }),
    setDetailColumnState: value => set({ detailColumnState: value }),
    scrollTop: null,
    detailScrollTop: null,
    setScrollTop: value => set({ scrollTop: value }),
    setDetailScrollTop: value => set({ detailScrollTop: value }),
    allCount: 0,
    setAllCount: value => set({ allCount: value }),

    initData: () => set({
        HRM2120_R01Filter: { ...defaultHRM2120_R01Filter, baseDt: today() },
        HRM2120_R01PastFilter: { ...defaultHRM2120_R01Filter, baseDt: today() },
        HRM2120_R01List: [],
        selectedHRM2120_R01: undefined,
        HRM2120_R01DetailList: [],
        selectedHRM2120_R01DetailList: [],
        deletedHRM2120_R01DetailList: [],
        originalHRM2120_R01DetailList: [],
        HRM2120_R01CalendarList: [],
        HRM2120_R01BulkForm: defaultHRM2120_R01BulkForm(),
        workShiftList: [],
        allWorkShiftCodes: [],
        columnState: null,
        detailColumnState: null,
        scrollTop: null,
        detailScrollTop: null,
        allCount: 0,
    }),
}));
